import api from './api';

/**
 * Service for public partner-related API calls
 * All methods return { success: true, data } or { success: false, error }
 */

const partnerService = {
  /**
   * Fetch the current partner's own profile (company info)
   */
  async fetchMyProfile() {
    try {
      const response = await api.get('/partners/me/');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching partner profile:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch profile'
      };
    }
  },

  /**
   * Update the current partner's profile
   */
  async updateMyProfile(data) {
    try {
      const response = await api.patch('/partners/me/', data);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating partner profile:', error);
      return {
        success: false,
        error: error.response?.data || 'Failed to update profile'
      };
    }
  },

  /**
   * Fetch all verified partners
   * @param {Object} params - Query parameters (city, search)
   */
  async fetchPartners(params = {}) {
    try {
      const response = await api.get('/partners/', { params });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching partners:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch partners'
      };
    }
  },

  /**
   * Fetch partner detail by ID (public - verified partners only)
   * @param {number} partnerId
   */
  async fetchPartnerById(partnerId) {
    try {
      const response = await api.get(`/partners/${partnerId}/`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching partner:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Partner not found'
      };
    }
  },

  // ============================================
  // PARTNER INVOICES (Partner can view their invoices)
  // ============================================

  /**
   * Fetch partner's invoices
   */
  async fetchMyInvoices() {
    try {
      const response = await api.get('/partners/invoices/');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching invoices:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch invoices'
      };
    }
  },

  /**
   * Fetch single invoice detail
   */
  async fetchInvoiceById(invoiceId) {
    try {
      const response = await api.get(`/partners/invoices/${invoiceId}/`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching invoice:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Invoice not found'
      };
    }
  },

  // ============================================
  // ADMIN INVOICE MANAGEMENT
  // ============================================

  /**
   * Admin: Fetch all invoices
   */
  async adminFetchAllInvoices(params = {}) {
    try {
      const response = await api.get('/partners/admin/invoices/', { params });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching invoices:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch invoices'
      };
    }
  },

  /**
   * Admin: Fetch invoices for a specific partner
   */
  async adminFetchPartnerInvoices(partnerId) {
    try {
      const response = await api.get(`/partners/admin/${partnerId}/invoices/`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching partner invoices:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch invoices'
      };
    }
  },

  /**
   * Admin: Upload new invoice (PDF)
   */
  async adminUploadInvoice(invoiceData) {
    try {
      const formData = new FormData();
      Object.keys(invoiceData).forEach(key => {
        if (invoiceData[key] !== null && invoiceData[key] !== undefined && invoiceData[key] !== '') {
          formData.append(key, invoiceData[key]);
        }
      });

      const response = await api.post('/partners/admin/invoices/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error uploading invoice:', error);
      return {
        success: false,
        error: error.response?.data || 'Failed to upload invoice'
      };
    }
  },

  /**
   * Admin: Update invoice
   */
  async adminUpdateInvoice(invoiceId, invoiceData) {
    try {
      const formData = new FormData();
      Object.keys(invoiceData).forEach(key => {
        if (invoiceData[key] !== null && invoiceData[key] !== undefined && invoiceData[key] !== '') {
          formData.append(key, invoiceData[key]);
        }
      });

      const response = await api.patch(`/partners/admin/invoices/${invoiceId}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating invoice:', error);
      return {
        success: false,
        error: error.response?.data || 'Failed to update invoice'
      };
    }
  },

  /**
   * Admin: Delete invoice
   */
  async adminDeleteInvoice(invoiceId) {
    try {
      await api.delete(`/partners/admin/invoices/${invoiceId}/`);
      return { success: true };
    } catch (error) {
      console.error('Error deleting invoice:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to delete invoice'
      };
    }
  },

  /**
   * Admin: Change partner user (transfer ownership to another client user)
   */
  async adminChangePartnerUser(partnerId, newUserId) {
    try {
      const response = await api.post(`/partners/admin/${partnerId}/change-user/`, {
        new_user_id: newUserId
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error changing partner user:', error);
      const errorData = error.response?.data;
      let errorMessage = 'Failed to change partner user';
      if (errorData?.new_user_id) {
        errorMessage = errorData.new_user_id[0] || errorMessage;
      } else if (errorData?.error) {
        errorMessage = errorData.error;
      }
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  // ============================================
  // IMPACT PARTNER SETTINGS
  // ============================================

  /**
   * Get Impact Partner settings
   */
  async getImpactSettings() {
    try {
      const response = await api.get('/partners/impact-settings/');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching impact settings:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch impact settings'
      };
    }
  },

  /**
   * Update Impact Partner settings
   * @param {Object} data - { is_impact_partner, agree_to_terms }
   */
  async updateImpactSettings(data) {
    try {
      const response = await api.patch('/partners/impact-settings/', data);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating impact settings:', error);
      const errorData = error.response?.data;
      let errorMessage = 'Failed to update impact settings';
      if (errorData?.agree_to_terms) {
        errorMessage = errorData.agree_to_terms[0] || errorMessage;
      } else if (errorData?.detail) {
        errorMessage = errorData.detail;
      }
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  // ============================================
  // ITEM PACKAGES (Partner can purchase extra item slots)
  // ============================================

  /**
   * Get available item packages
   */
  async fetchAvailablePackages() {
    try {
      const response = await api.get('/partners/packages/');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching packages:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch packages'
      };
    }
  },

  /**
   * Get partner's current item usage and limits
   */
  async fetchItemsInfo() {
    try {
      const response = await api.get('/partners/packages/info/');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching items info:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch items info'
      };
    }
  },

  /**
   * Purchase an item package (creates Stripe checkout session)
   * @param {string} packageType - 'PACK_5' or 'PACK_10'
   */
  async purchasePackage(packageType) {
    try {
      const response = await api.post('/partners/packages/purchase/', {
        package_type: packageType
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error purchasing package:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create checkout session'
      };
    }
  },

  /**
   * Verify package purchase after Stripe redirect
   * @param {string} sessionId - Stripe checkout session ID
   */
  async verifyPackagePurchase(sessionId) {
    try {
      const response = await api.post('/partners/packages/verify/', {
        session_id: sessionId
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error verifying package:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to verify purchase'
      };
    }
  },

  /**
   * Get package purchase history
   */
  async fetchPackageHistory() {
    try {
      const response = await api.get('/partners/packages/history/');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching package history:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch package history'
      };
    }
  },

  /**
   * Request a bundle upgrade (requires admin approval)
   * @param {string} packageType - Bundle type (BUNDLE_5, BUNDLE_10, BUNDLE_15, BUNDLE_40)
   * @param {string} notes - Optional notes for the request
   */
  async requestBundle(packageType, notes = '') {
    try {
      const response = await api.post('/partners/bundles/request/', {
        package_type: packageType,
        notes: notes
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error requesting bundle:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to submit bundle request'
      };
    }
  }
};

export default partnerService;
